import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const NavigationDrawer = ({ isOpen, onClose, handlePageChange, currentPage, user, LucideIcons, unreadNotificationsCount, canAccessDashboard, t }) => {
    
    // Quick helper for nav items
    const NavItem = ({ page, icon: Icon, label, badge, action }) => {
        const isActive = currentPage === page;
        return (
            <button
                onClick={() => {
                    if (action) action();
                    else handlePageChange(page);
                }}
                className={`w-full flex items-center justify-between p-4 rounded-xl transition-all duration-300 ${
                    isActive 
                    ? 'bg-white/10 border border-white/20 shadow-purple-glow text-white' 
                    : 'text-gray-400 hover:bg-white/5 hover:text-white border border-transparent'
                }`}
            >
                <div className="flex items-center gap-4">
                    <Icon size={22} className={isActive ? 'text-purple-400' : ''} />
                    <span className="font-medium text-lg">{label}</span>
                </div>
                {badge > 0 && (
                    <div className="bg-purple-600 text-white text-xs font-bold px-2 py-1 rounded-full">
                        {badge}
                    </div>
                )}
            </button>
        );
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100]"
                    />
                    
                    {/* Drawer */}
                    <motion.div
                        initial={{ x: '100%' }}
                        animate={{ x: 0 }}
                        exit={{ x: '100%' }}
                        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                        className="fixed top-0 right-0 h-full w-[85vw] max-w-sm glass-panel z-[101] flex flex-col border-l border-white/10"
                    >
                        <div className="flex items-center justify-between p-6 border-b border-white/10">
                            <div className="flex items-center gap-3">
                                <img src="/logo.png" alt="Whispers of Harmony" className="h-8 w-auto drop-shadow-[0_0_8px_rgba(139,92,246,0.5)]" />
                                <h2 className="text-2xl font-playfair font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-sky-400">
                                    Menu
                                </h2>
                            </div>
                            <button 
                                onClick={onClose}
                                className="p-2 rounded-full hover:bg-white/10 text-gray-400 hover:text-white transition-colors"
                            >
                                <LucideIcons.X size={24} />
                            </button>
                        </div>
                        
                        <div className="flex-1 overflow-y-auto p-4 space-y-2 custom-scrollbar">
                            <NavItem page="anonymousFeed" icon={LucideIcons.Feather} label={t('nav.feed')} />
                            {user && !user.isAnonymous && (
                                <>
                                    <div className="my-4 border-t border-white/10 pt-4 px-2 text-xs font-bold text-gray-500 uppercase tracking-wider">Social</div>
                                    <NavItem page="nexus" icon={LucideIcons.AppWindow} label={t('nav.nexus')} />
                                    <NavItem page="innerCircleFeed" icon={LucideIcons.Star} label={t('nav.innerCircle')} />
                                    <NavItem page="connectedFeed" icon={LucideIcons.HeartHandshake} label={t('nav.connected')} />
                                    <NavItem page="users" icon={LucideIcons.Users} label={t('nav.users')} />
                                    
                                    <div className="my-4 border-t border-white/10 pt-4 px-2 text-xs font-bold text-gray-500 uppercase tracking-wider">Games & Economy</div>
                                    <NavItem page="walletHub" icon={LucideIcons.Wallet} label={t('nav.myHub')} />
                                    <NavItem page="showdown" icon={LucideIcons.Swords} label={t('nav.showdown')} />
                                    <NavItem page="leaderboards" icon={LucideIcons.Trophy} label={t('nav.leaders')} />
                                    
                                    <div className="my-4 border-t border-white/10 pt-4 px-2 text-xs font-bold text-gray-500 uppercase tracking-wider">Media</div>
                                    <NavItem page="moments" icon={LucideIcons.PlaySquare} label={t('nav.moments')} />
                                    <NavItem page="myMoments" icon={LucideIcons.UserSquare} label={t('nav.myMoments')} />
                                    
                                    <div className="my-4 border-t border-white/10 pt-4 px-2 text-xs font-bold text-gray-500 uppercase tracking-wider">Account</div>
                                    <NavItem page="notifications" icon={LucideIcons.Bell} label={t('nav.notifications')} badge={unreadNotificationsCount} />
                                    <NavItem page="settings" icon={LucideIcons.Settings} label={t('nav.settings')} />
                                    {canAccessDashboard && (
                                        <NavItem page="dashboard" icon={LucideIcons.ShieldCheck} label={t('nav.dashboard')} />
                                    )}
                                </>
                            )}
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
};

export default NavigationDrawer;
